// src/pages/payment/PaymentPage.js - Clear Filters aligned with Filters heading
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getPaymentTransactions } from '../../api/paymentTransactionAPI';
import { getCompanies } from '../../api/companyAPI';
import { getSites } from '../../api/siteAPI';
import { getChargers } from '../../api/chargerAPI';
import { getDrivers } from '../../api/driverAPI';
import PaymentTransactionList from '../../components/payment/PaymentTransactionList';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const paymentStatuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'refunded', label: 'Refunded' }
];

const PaymentPage = () => {
  const [filters, setFilters] = useState({
    company_id: '',
    site_id: '',
    charger_id: '',
    driver_id: '',
    status: ''
  });

  // Fetch companies
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Fetch sites based on selected company
  const { data: sites } = useQuery({
    queryKey: ['sites', filters.company_id],
    queryFn: () => getSites(filters.company_id),
    enabled: !!filters.company_id
  });

  // Fetch chargers based on selected company and site
  const { data: chargers } = useQuery({
    queryKey: ['chargers', filters.company_id, filters.site_id],
    queryFn: () => getChargers({ 
      company_id: filters.company_id,
      site_id: filters.site_id 
    }),
    enabled: !!filters.company_id
  });

  // Fetch drivers based on selected company
  const { data: drivers } = useQuery({
    queryKey: ['drivers', filters.company_id],
    queryFn: () => getDrivers(),
    enabled: !!filters.company_id
  });

  // Fetch payment transactions with filters
  const { 
    data: paymentTransactions, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['payment-transactions', filters],
    queryFn: () => {
      const params = {};
      if (filters.company_id) params.company_id = parseInt(filters.company_id);
      if (filters.site_id) params.site_id = parseInt(filters.site_id);
      if (filters.charger_id) params.charger_id = parseInt(filters.charger_id);
      if (filters.driver_id) params.driver_id = parseInt(filters.driver_id);
      if (filters.status) params.status = filters.status;
      
      return getPaymentTransactions(params);
    }
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Reset dependent filters when parent changes
      if (field === 'company_id') {
        newFilters.site_id = '';
        newFilters.charger_id = '';
        newFilters.driver_id = '';
      } else if (field === 'site_id') {
        newFilters.charger_id = '';
      }
      
      return newFilters;
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  const clearFilters = () => {
    setFilters({
      company_id: '',
      site_id: '',
      charger_id: '',
      driver_id: '',
      status: ''
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payment Transactions</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Filters</Typography>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={2.4}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={filters.company_id}
                label="Company"
                onChange={(e) => handleFilterChange('company_id', e.target.value)}
              >
                <MenuItem value="">All Companies</MenuItem>
                {companies?.map((company) => (
                  <MenuItem key={company.CompanyId} value={company.CompanyId}>
                    {company.CompanyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={2.4}>
            <FormControl fullWidth disabled={!filters.company_id}>
              <InputLabel>Site</InputLabel>
              <Select
                value={filters.site_id}
                label="Site"
                onChange={(e) => handleFilterChange('site_id', e.target.value)}
              >
                <MenuItem value="">All Sites</MenuItem>
                {sites?.map((site) => (
                  <MenuItem key={site.SiteId} value={site.SiteId}>
                    {site.SiteName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={2.4}>
            <FormControl fullWidth disabled={!filters.company_id}>
              <InputLabel>Charger</InputLabel>
              <Select
                value={filters.charger_id}
                label="Charger"
                onChange={(e) => handleFilterChange('charger_id', e.target.value)}
              >
                <MenuItem value="">All Chargers</MenuItem>
                {chargers?.map((charger) => (
                  <MenuItem key={charger.ChargerId} value={charger.ChargerId}>
                    {charger.ChargerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={2.4}>
            <FormControl fullWidth disabled={!filters.company_id}>
              <InputLabel>Driver</InputLabel>
              <Select
                value={filters.driver_id}
                label="Driver"
                onChange={(e) => handleFilterChange('driver_id', e.target.value)}
              >
                <MenuItem value="">All Drivers</MenuItem>
                {drivers?.map((driver) => (
                  <MenuItem key={driver.DriverId} value={driver.DriverId}>
                    {driver.DriverFullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={2.4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {paymentStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Transactions List */}
      {isLoading ? (
        <LoadingSpinner />
      ) : isError ? (
        <ErrorAlert message={error instanceof Error ? error.message : 'Error loading payment transactions'} />
      ) : (
        <PaymentTransactionList 
          paymentTransactions={paymentTransactions || []}
          isLoading={isLoading}
          isError={isError}
          error={error}
        />
      )}
    </Box>
  );
};

export default PaymentPage;